import './FlashNotification.scss';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';

import { cxHelpers } from 'util/className';
import { sanitizeString } from 'util/formatter';

const { cx, cxEl } = cxHelpers('FlashNotification');
import Icon from 'fe-design-base/atoms/Icon';
import Text from 'fe-design-base/atoms/Text';

const DEFAULT_AUTO_HIDE_TIMEOUT = 5000;

const ICON_MAP = {
  info: <Icon iconName="Info" color="blue300" mr={6} mt={2} />,
  notice: <Icon iconName="Success" color="green300" mr={6} mt={2} />,
  warning: <Icon iconName="Warning" color="orange300" mr={6} mt={2} />,
  error: <Icon iconName="Critical" color="red300" mr={6} mt={2} />,
};

interface FlashOptions {
  autoHide?: boolean;
  autoHideTimeout?: number;
}

interface ShowDetail {
  type: keyof typeof ICON_MAP;
  text: string;
  options?: FlashOptions;
}

interface ReactFlashNoticeGlobal {
  show: (detail: ShowDetail) => void;
  hide: () => void;
}

declare global {
  interface Window {
    ReactFlashNotice?: ReactFlashNoticeGlobal;
  }
}

const DEFAULT_OPTIONS: FlashOptions = {
  autoHide: true,
  autoHideTimeout: DEFAULT_AUTO_HIDE_TIMEOUT,
};

const FlashNotification = () => {
  const [visible, setVisible] = useState(false);
  const [type, setType] = useState('');
  const [text, setText] = useState('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const hide = useCallback(() => {
    setVisible(false);
    // Clear timeout when manually hiding
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (
        event.key === 'Escape' ||
        event.key === 'Enter' ||
        event.key === ' '
      ) {
        event.preventDefault();
        hide();
      }
    },
    [hide]
  );

  const show = useCallback(
    (detail: ShowDetail) => {
      const { type: noticeType, text: message, options } = detail;
      const opts = { ...DEFAULT_OPTIONS, ...options };
      if (window.Homebase?.FlashNotice?.hide) {
        window.Homebase.FlashNotice.hide();
      }

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setType(noticeType);
      setText(message);
      setVisible(true);

      if (opts.autoHide) {
        timeoutRef.current = setTimeout(hide, opts.autoHideTimeout);
      }
    },
    [hide]
  );

  useEffect(() => {
    window.ReactFlashNotice = { show, hide };

    const handleShow = (event: Event) => {
      const detail = (event as CustomEvent<ShowDetail>).detail;
      show(detail);
    };
    const handleHide = () => hide();

    document.addEventListener(
      'reactFlashNotice:show',
      handleShow as EventListener
    );
    document.addEventListener('reactFlashNotice:hide', handleHide);

    return () => {
      document.removeEventListener(
        'reactFlashNotice:show',
        handleShow as EventListener
      );
      document.removeEventListener('reactFlashNotice:hide', handleHide);
      delete window.ReactFlashNotice;

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [show, hide]);

  const ariaRole = useMemo(
    () => (type === 'error' || type === 'warning' ? 'alert' : 'status'),
    [type]
  );

  const ariaLive = useMemo(
    () => (type === 'error' || type === 'warning' ? 'assertive' : 'polite'),
    [type]
  );

  return (
    <div
      className={classnames(cx(), cxEl(type), cx({ visible }))}
      onClick={hide}
      role={ariaRole}
      aria-live={ariaLive}
      aria-label={`${type} notification. Press Enter or Escape to dismiss.`}
    >
      <div
        className={cxEl('content')}
        onKeyDown={handleKeyDown}
        tabIndex={visible ? 0 : -1}
        role="button"
        aria-label="Dismiss notification"
      >
        {type && ICON_MAP[type as keyof typeof ICON_MAP]
          ? ICON_MAP[type as keyof typeof ICON_MAP]
          : null}

        <Text component="span" asHTML>
          {sanitizeString(text)}
        </Text>
      </div>
    </div>
  );
};

export default FlashNotification;
